<template>
  <div class="subject-configuration">
    <div class="content-container">
      <div class="header-section">
        <h1 class="main-title">PsySection</h1>
        <p class="main-subtitle">Begin your Competency Framework configuration journey</p>
      </div>

      <div class="form-container">
        <form @submit.prevent="handleSubmit" novalidate>
          <!-- Subject Name -->
          <div class="field-group">
            <label class="field-label">PsySection Name</label>
            <input
              type="text"
              class="field-input"
              :class="{
                'field-valid': localFormData.subjectName && !errors.subjectName,
                'field-invalid': errors.subjectName
              }"
              v-model="localFormData.subjectName"
              placeholder="Enter PsySection name"
              @blur="validateField('subjectName')"
              @input="clearError('subjectName')"
            />
            <div v-if="errors.subjectName" class="field-error">
              {{ errors.subjectName }}
            </div>
          </div>

          <!-- Job Level -->
          <div class="field-group">
            <label class="field-label">Job Level</label>
            <div class="select-wrapper">
              <select
                class="field-select"
                :class="{
                  'field-valid': localFormData.jobLevel && !errors.jobLevel,
                  'field-invalid': errors.jobLevel
                }"
                v-model="localFormData.jobLevel"
                @change="validateField('jobLevel')"
              >
                <option value="">Select Job Level</option>
                <option value="entry">Entry Level</option>
                <option value="junior">Junior Level</option>
                <option value="intermediate">Intermediate Level</option>
                <option value="senior">Senior Level</option>
                <option value="lead">Lead Level</option>
                <option value="manager">Manager Level</option>
                <option value="director">Director Level</option>
                <option value="executive">Executive Level</option>
              </select>
              <i class="bi bi-check-lg select-check" v-if="localFormData.jobLevel && !errors.jobLevel"></i>
              <i class="bi bi-chevron-down select-arrow"></i>
            </div>
            <div v-if="errors.jobLevel" class="field-error">
              {{ errors.jobLevel }}
            </div>
          </div>

          <!-- Job Family -->
          <div class="field-group">
            <label class="field-label">Job Family</label>
            <div class="select-wrapper">
              <select
                class="field-select"
                :class="{
                  'field-valid': localFormData.jobFamily && !errors.jobFamily,
                  'field-invalid': errors.jobFamily
                }"
                v-model="localFormData.jobFamily"
                @change="validateField('jobFamily')"
              >
                <option value="">Select Job Family</option>
                <option value="hr">Human Resources</option>
                <option value="tech">Technology</option>
                <option value="banking">Banking & Finance</option>
                <option value="sales">Sales & Marketing</option>
                <option value="operations">Operations</option>
                <option value="consulting">Consulting</option>
                <option value="healthcare">Healthcare</option>
                <option value="education">Education</option>
                <option value="legal">Legal</option>
                <option value="customer-service">Customer Service</option>
                <option value="manufacturing">Manufacturing</option>
                <option value="retail">Retail</option>
                <option value="logistics">Logistics & Supply Chain</option>
                <option value="research">Research & Development</option>
                <option value="design">Design & Creative</option>
              </select>
              <i class="bi bi-check-lg select-check" v-if="localFormData.jobFamily && !errors.jobFamily"></i>
              <i class="bi bi-chevron-down select-arrow"></i>
            </div>
            <div v-if="errors.jobFamily" class="field-error">
              {{ errors.jobFamily }}
            </div>
          </div>

          <!-- Report Type -->
          <div class="field-group">
            <label class="field-label">Report Type</label>
            <div class="select-wrapper">
              <select
                class="field-select"
                :class="{
                  'field-valid': localFormData.reportType && !errors.reportType,
                  'field-invalid': errors.reportType
                }"
                v-model="localFormData.reportType"
                @change="validateField('reportType')"
              >
                <option value="" disabled>Select Report type</option>
                <option value="1">Report 1</option>
                <option value="2">Report 2</option>
              </select>
              <i class="bi bi-check-lg select-check" v-if="localFormData.reportType && !errors.reportType"></i>
              <i class="bi bi-chevron-down select-arrow"></i>
            </div>
            <div v-if="errors.reportType" class="field-error">
              {{ errors.reportType }}
            </div>
          </div>

          <div class="helper-text">
            This information helps us customize your experience
          </div>

          <div class="button-container">
            <button
              type="submit"
              class="next-button"
              :disabled="!isFormValid || isSubmitting"
            >
              <span v-if="isSubmitting">
                <span class="spinner"></span>
                Processing...
              </span>
              <span v-else>
                Next
                <i class="bi bi-arrow-right"></i>
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SubjectConfiguration',
  props: {
    formData: {
      type: Object,
      default: () => ({
        subjectName: '',
        jobLevel: '',
        jobFamily: '',
        reportType: ''
      })
    }
  },
  emits: ['update-data', 'step-completed'],
  data() {
    return {
      localFormData: JSON.parse(JSON.stringify({
        subjectName: '',
        jobLevel: '',
        jobFamily: '',
        reportType: ''
      })),
      errors: {},
      isSubmitting: false,
      validationRules: {
        subjectName: { required: true, minLength: 3, maxLength: 100 },
        jobLevel: { required: true },
        jobFamily: { required: true },
        reportType: { required: true }
      }
    }
  },
  computed: {
    isFormValid() {
      return Object.keys(this.errors).length === 0 &&
             this.localFormData.subjectName &&
             this.localFormData.jobLevel &&
             this.localFormData.jobFamily &&
             this.localFormData.reportType
    }
  },
  watch: {
    formData: {
      handler(newFormData) {
        if (newFormData) {
          const merged = { ...this.localFormData, ...newFormData };
          const changed = JSON.stringify(this.localFormData) !== JSON.stringify(merged);
          if (changed) {
            this.localFormData = merged;
          }
        }
      },
      deep: true,
      immediate: true
    },
    localFormData: {
      handler(newData) {
        this.$emit('update-data', {
          stepKey: 'subjectConfiguration',
          data: newData
        });
      },
      deep: true
    }
  },
  methods: {
    validateField(fieldName) {
      const value = this.localFormData[fieldName];
      const rules = this.validationRules[fieldName];

      if (!rules) return;

      if (this.errors[fieldName]) {
        delete this.errors[fieldName];
      }

      if (rules.required && (!value || (typeof value === 'string' && !value.trim()))) {
        this.errors[fieldName] = `${this.getFieldLabel(fieldName)} is required`;
        return;
      }

      if (typeof value === 'string' && value && rules.minLength && value.length < rules.minLength) {
        this.errors[fieldName] = `${this.getFieldLabel(fieldName)} must be at least ${rules.minLength} characters`;
        return;
      }

      if (typeof value === 'number' && rules.min && value < rules.min) {
        this.errors[fieldName] = `${this.getFieldLabel(fieldName)} must be at least ${rules.min}`;
        return;
      }
    },
    clearError(fieldName) {
      if (this.errors[fieldName]) {
        delete this.errors[fieldName];
      }
    },
    getFieldLabel(fieldName) {
      const labels = {
        subjectName: 'Subject Name',
        jobLevel: 'Job Level',
        jobFamily: 'Job Family',
        reportType: 'Report Type'
      };
      return labels[fieldName] || fieldName;
    },
    validateAllFields() {
      Object.keys(this.validationRules).forEach(field => {
        this.validateField(field);
      });
    },
    async handleSubmit() {
      this.validateAllFields();

      if (!this.isFormValid) {
        this.showNotification('Please fix all validation errors before proceeding', 'error');
        return;
      }

      this.isSubmitting = true;

      try {
        await new Promise(resolve => setTimeout(resolve, 1000));
        this.$emit('step-completed', 1);
        this.showNotification('PsySection configuration completed successfully!', 'success');
      } catch (error) {
        this.showNotification('Failed to save configuration. Please try again.', 'error');
      } finally {
        this.isSubmitting = false;
      }
    },
    showNotification(message, type = 'info') {
      const alertClass = {
        success: 'alert-success',
        error: 'alert-danger',
        info: 'alert-info',
        warning: 'alert-warning'
      }[type] || 'alert-info';

      const notification = document.createElement('div');
      notification.className = `alert ${alertClass} alert-dismissible fade show position-fixed`;
      notification.style.cssText = 'top: 20px; right: 20px; z-index: 9999; min-width: 300px;';
      notification.innerHTML = `${message}<button type="button" class="btn-close" data-bs-dismiss="alert"></button>`;

      document.body.appendChild(notification);
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 5000);
    }
  }
}
</script>

<style scoped>
.subject-configuration {
  min-height: 100vh;
  background-color: #f8f9fa;
  padding: 0;
}

.content-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem 1rem;
}

.header-section {
  text-align: center;
  margin-bottom: 3rem;
}

.main-title {
  font-size: 2.5rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.main-subtitle {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
}

.form-container {
  background: white;
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.field-group {
  margin-bottom: 2rem;
}

.field-label {
  display: block;
  font-size: 1rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.75rem;
}

.field-input,
.field-select {
  width: 100%;
  padding: 1rem;
  font-size: 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background-color: #fff;
  transition: all 0.2s ease;
  outline: none;
}

.field-input:focus,
.field-select:focus {
  border-color: #06d6a0;
  box-shadow: 0 0 0 3px rgba(6, 214, 160, 0.1);
}

.field-input.field-valid,
.field-select.field-valid {
  border-color: #06d6a0;
}

.field-input.field-invalid,
.field-select.field-invalid {
  border-color: #ef4444;
}

/* COMMENTED OUT - Time related styles */
/*
.time-inputs {
  display: flex;
  gap: 1rem;
}

.time-input-group {
  flex: 1;
  position: relative;
}

.time-input {
  padding-right: 3rem;
}

.time-label {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
  pointer-events: none;
}
*/

.select-wrapper {
  position: relative;
}

.field-select {
  appearance: none;
  padding-right: 3rem;
  cursor: pointer;
}

.select-check {
  position: absolute;
  right: 2.5rem;
  top: 50%;
  transform: translateY(-50%);
  color: #06d6a0;
  font-size: 1.2rem;
  pointer-events: none;
}

.select-arrow {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
}

.field-error {
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

.helper-text {
  color: #6b7280;
  font-size: 0.875rem;
  margin-bottom: 2rem;
}

.button-container {
  text-align: right;
}

.next-button {
  background-color: #8b5cf6;
  color: white;
  border: none;
  padding: 0.875rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.next-button:hover:not(:disabled) {
  background-color: #7c3aed;
  transform: translateY(-1px);
}

.next-button:disabled {
  background-color: #d1d5db;
  color: #9ca3af;
  cursor: not-allowed;
  transform: none;
}

.spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 768px) {
  .content-container {
    padding: 1rem;
  }
  
  .form-container {
    padding: 2rem;
  }
  
  .main-title {
    font-size: 2rem;
  }
  
  /* COMMENTED OUT - Time inputs responsive styles */
  /*
  .time-inputs {
    flex-direction: column;
    gap: 1rem;
  }
  */
  
  .next-button {
    width: 100%;
    justify-content: center;
  }
}
</style>